import { Component, Input, OnChanges } from '@angular/core';
import {
  ApexAnnotations,
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexMarkers,
  ApexStroke,
  ApexTitleSubtitle,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis
} from "ng-apexcharts";
import tinycolor from "tinycolor2";

import { SharedService } from '@app/core';

type ChartOptions = {
  series: ApexAxisChartSeries;
  annotations: ApexAnnotations;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  markers: ApexMarkers;
  labels: string[];
  stroke: ApexStroke;
  title: ApexTitleSubtitle;
  legend: ApexLegend;
  colors: string[];
  tooltip: ApexTooltip;
  fill: ApexFill;
};

@Component({
  selector: 'app-line-chart',
  templateUrl: './line.component.html',
  styleUrls: ['./line.component.scss']
})
export class LineChartComponent implements OnChanges {
  @Input() series: ApexAxisChartSeries;
  chartOptions: Partial<ChartOptions>;

  constructor(private sharedService: SharedService) { }

  ngOnChanges() {
    if (this.series) {
      const productCounts = this.series.reduce((supplierCount, series: { name: string, data: any[] }) => 'data' in series && series['data']?.['length'] ? {
        ...supplierCount,
        [series?.['data'][0]?.meta.product]: (supplierCount?.[series?.['data'][0].meta.product] ?? 0) + 1
      } : supplierCount, {});
      const colorRequired: number[] = Object.values(productCounts);

      const customColors = ["#F37D6D", "#7CADD4", "#AFD362", "#AE59A1", "#D79428", "#FDD24F", "#9593B1", "#F181B3", "#5EA390", "#BFC0BB"];
      const customColorLength = customColors.length;
      let colors = colorRequired.reduce((colors, count, i) => [
        ...colors,
        ...this.getColorShades(customColors[i % customColorLength], count)
      ], []);

      const minXValue = Math.min(...this.series.map(series => Math.min(...series['data'].map(data => data.x)))) - 5 * 24 * 60 * 60 * 1000;
      const maxXValue = Math.max(...this.series.map(series => Math.max(...series['data'].map(data => data.x)))) + 5 * 24 * 60 * 60 * 1000;
      const minYValueTemp = Math.min(...this.series.map(series => Math.min(...series['data'].map(data => data.y))));
      const maxYValueTemp = Math.max(...this.series.map(series => Math.max(...series['data'].map(data => data.y))));
      const minYValue = maxYValueTemp - minYValueTemp < 10 ? minYValueTemp - 5 : minYValueTemp;
      const maxYValue = maxYValueTemp - minYValueTemp < 10 ? minYValueTemp + 5 : maxYValueTemp;

      this.chartOptions = {
        series: this.series,
        chart: {
          height: 350,
          width: '100%',
          type: "line"
        },
        fill: {
          type: "solid",
          gradient: {
            shadeIntensity: 1,
            inverseColors: false,
            opacityFrom: 0.45,
            opacityTo: 0.05,
            stops: [20, 100, 100, 100]
          }
        },
        colors: colors,
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        grid: {
          padding: {
            right: 30,
            left: 20
          }
        },
        markers: {
          size: 4, // Adjust the size as needed
          hover: {
            sizeOffset: 4 // Adjust the size offset as needed
          }
        },
        xaxis: {
          type: "datetime",
          tickAmount: 8,
          min: minXValue,
          max: maxXValue,
          title: {
            text: "Time Duration",
            offsetY: 3
          },
          labels: {
            rotate: -15,
            // rotateAlways: true,
            formatter: function (val, timestamp) {
              const date = new Date(timestamp);

              return date.toLocaleDateString('en-IN', { day: '2-digit' }) + ' ' +
                date.toLocaleDateString('en-IN', { month: 'short' }) + ' ' +
                date.toLocaleDateString('en-IN', { year: 'numeric' });
            }
          }
        },
        yaxis: {
          min: this.sharedService.roundDown(minYValue),
          max: this.sharedService.roundUp(maxYValue),
          title: {
            text: "Item Rate",
            offsetX: 0
          },
          labels: {
            formatter: (value: number) => this.sharedService.formatNumber(value, true)
          }
        },
        legend: {
          show: true, // Set to true to show the legend even with one series
          showForSingleSeries: true, // Set to true to show the legend even with one series
          offsetY: 6,
        },
        tooltip: {
          enabled: true,
          followCursor: true,
          fillSeriesColor: true,
          theme: 'light',
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
            const color = w.config.colors[seriesIndex];

            return '<div class="arrow_box">' +
              '<div class="apexcharts-tooltip-title apxcharts-active">' +
              '<span class="color-item" style="background-color:' + color + '" ></span>' +
              this.sharedService.capitalize(data.meta?.product) +
              '</div>' +
              '<div class="custom-tooltip-data"> Item Rate: <strong> ' + this.sharedService.formatNumber(series[seriesIndex][dataPointIndex], true, 0, 2, true) + ' </strong></div>' +
              (data.meta?.supplier ? '<div class="custom-tooltip-data"> Supplier: <strong>' + this.sharedService.capitalize(data.meta?.supplier) + '</strong></div>' : '') +
              '<div class="custom-tooltip-data"> Quantity: <strong>' + this.sharedService.formatNumber(data.meta?.quantity, false, 0, 2, true) + ' ' + data.meta?.unit + '</strong></div>' +
              '<div class="custom-tooltip-data"> Latest Rate: <strong>' + this.sharedService.formatNumber(data.meta?.latestRate, true, 0, 2, true) + '</strong></div>' +
              '</div>';
          }
        }
      };
    }
  }

  getColorShades(color: string, shadeCount: number): string[] {
    if (!color || shadeCount <= 0) {
      return [];
    }

    const shades: string[] = [color];
    const convertedColor = tinycolor(color);

    for (let i = 2; i <= shadeCount; i++) {
      shades.push(convertedColor.darken(i * 20).toString());
    }

    return shades;
  }
}